
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ManagerHeader from "./ManagerHeader";
import Navbar from "./Navbar";

const AffiliateCsvUpload = () => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const navigate = useNavigate();

  // Check authentication on page load
  useEffect(() => {
    const token = localStorage.getItem('mangertoken');
    if (!token) {
      navigate('/ManagerLogin');
    }
  }, [navigate]);

  // Fetch CSV data on component mounts
  useEffect(() => {
    fetchCsvData();
  }, []);

  const fetchCsvData = async () => {
    try {
      const response = await fetch("https://server-side-influencer.onrender.com/manager/get-affiliate-csv");
      const result = await response.json();
      if (response.ok) {
        setCsvData(result.data);
      } else {
        toast.error(`Failed to fetch CSV Data: ${result.message || "Something went wrong."}`);
      }
    } catch (error) {
      toast.error("Network error: Unable to fetch CSV data.");
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!file) {
      toast.error("Please select a CSV file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch("https://server-side-influencer.onrender.com/manager/upload-affiliate-csv", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        toast.success("Affiliate CSV uploaded successfully!");
        setFile(null);
        fetchCsvData();
      } else {
        toast.error(`Upload Failed: ${result.message || "Something went wrong."}`);
      }
    } catch (error) {
      toast.error("Network error: Unable to connect to server.");
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <div className="text-white fixed top-0 left-0 bottom-0 z-20">
        <Navbar />
      </div>

      <div className="ml-0 w-full">
        {/* ✅ Add this fixed top-right red label */}
        <div className="fixed top-4 right-4 z-50 flex items-center space-x-1 text-red-600 font-semibold text-sm cursor-pointer"  style={{ top: '55%' }}     onClick={() => navigate('/influencerearnings')}     > 
          <span>Influencer Total Clicks and Earnings</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </div>
        {/* End of top-right label */}

        <div className="fixed top-0 left-0 right-0 z-10 bg-white shadow-md">
          <ManagerHeader page="Affiliate CSV Upload" />
        </div>

        <div className="mt-10 flex flex-col items-center justify-center mt-12">
          <h2 className="text-lg font-semibold mb-2 mt-12">
            Upload Affiliate Performance CSV
          </h2>

          <div
            className="flex justify-center items-center w-[300px] sm:w-[400px] h-[150px] sm:h-[200px] p-4 mt-5"
            style={{
              boxSizing: 'border-box',
              border: '4px solid',
              borderImage: 'linear-gradient(to right, #8B5CF6, #3B82F6) 1',
              borderRadius: '16px',
            }}
          >
            <form onSubmit={handleUpload} className="flex flex-col items-center w-full h-full justify-center">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="mb-4 p-2 border border-gray-300 rounded-lg w-full"
              />
              <button
                type="submit"
                className="text-white bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
              >
                Upload CSV
              </button>
            </form>
          </div>

          <p className="mt-4 text-sm text-gray-600 text-center px-6">
            Note: This CSV should contain tracking ID performance data like:<br />
            <code className="text-xs block mt-2 bg-gray-100 p-2 rounded-md">
              Tracking ID, Clicks, Items Ordered, Items Shipped, Revenue, Ad Fees
            </code>
          </p>
        </div>

        {/* Table to Display CSV Data */}
        <div className="mt-8 px-8 mt-12">
          <h3 className="text-center text-lg font-semibold mb-4">Affiliate CSV Data</h3>

          {csvData.length > 0 ? (
            <div className="overflow-x-auto ml-12"  >
              <table className="min-w-full border border-gray-300 text-sm  mt-12 " >
                <thead className="bg-gray-200">
                  <tr>
                    <th className="border px-4 py-2">Date</th>
                    <th className="border px-4 py-2">Tracking ID</th>
                    <th className="border px-4 py-2">Product Title</th>
                    <th className="border px-4 py-2">Clicks</th>
                    <th className="border px-4 py-2">Items Ordered</th>
                    <th className="border px-4 py-2">Items Shipped</th>
                    <th className="border px-4 py-2">Revenue (₹)</th>
                    <th className="border px-4 py-2">Commission Income (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {csvData.map((item, index) => (
                    <tr key={index} className="text-center">
                      <td className="border px-4 py-2">{item.date}</td>
                      <td className="border px-4 py-2">{item.trackingId}</td>
                      <td className="border px-4 py-2">{item.productTitle}</td>
                      <td className="border px-4 py-2">{item.clicks}</td>
                      <td className="border px-4 py-2">{item.itemsOrdered}</td>
                      <td className="border px-4 py-2">{item.itemsShipped}</td>
                      <td className="border px-4 py-2">₹ {item.revenue}</td>
                      <td className="border px-4 py-2">₹ {item.commissionIncome}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 mt-4">No CSV data available.</p>
          )}
        </div>
      </div>

      <ToastContainer
        position={toast.POSITION.BOTTOM_CENTER}
        autoClose={5000}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
      />
    </div>
  );
};

export default AffiliateCsvUpload;
